import type { JSX } from 'react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/features/auth/session';
import { useStudentGuardians } from '@/features/guardian/hooks';
import { InvitationSuccessDialog } from '@/features/guardian/invitation-success-dialog';
import { InviteGuardianDialog } from '@/features/guardian/invite-guardian-dialog';
import {
  ACCESS_SCOPE_LABELS,
  RELATIONSHIP_LABELS,
  STATUS_LABELS,
} from '@/features/guardian/labels';
import { RevokeGuardianButton } from '@/features/guardian/revoke-guardian-button';
import type { GuardianStatus, InviteGuardianResult } from '@/features/guardian/types';
import { useHasCapability } from '@/lib/capabilities';

function statusVariant(status: GuardianStatus): 'default' | 'secondary' | 'outline' {
  if (status === 'ACTIVE') {
    return 'default';
  }
  if (status === 'INVITED') {
    return 'secondary';
  }
  return 'outline';
}

/**
 * Guardian relationship management for one student. Hosted on the student
 * detail experience rather than a disconnected admin system (ADR-026's
 * supported Phase G-0 instruction: reuse the existing Student pages).
 *
 * Capability-driven (Phase G-1, Job 8/19): inviting requires
 * `students:manage` (`guardian.routes.ts`'s own gate on the invite route).
 * Revoking is offered both to a `students:manage` holder (the
 * administrative path) and to the guardian's own row (self-revoke) - the
 * backend resolves this same distinction from the authenticated caller at
 * request time (`guardian.controller.ts`'s doc comment); comparing
 * `guardian.guardianUserId` to the current user here only decides whether
 * to show the button, never whether the request succeeds.
 */
export function GuardianSection({ studentId }: { readonly studentId: string }): JSX.Element {
  const { data: guardians, isLoading, isError } = useStudentGuardians(studentId);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [successResult, setSuccessResult] = useState<InviteGuardianResult | null>(null);
  const canManage = useHasCapability('students:manage');
  const { user } = useAuth();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle>Wali</CardTitle>
        {canManage && (
          <Button
            type="button"
            onClick={() => {
              setInviteOpen(true);
            }}
          >
            Undang Wali
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertDescription>Gagal memuat daftar wali.</AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && guardians !== undefined && guardians.length === 0 && (
          <p className="text-sm text-muted-foreground">Belum ada wali yang terhubung.</p>
        )}

        {!isLoading && !isError && guardians !== undefined && guardians.length > 0 && (
          <ul className="flex flex-col gap-3">
            {guardians.map((guardian) => (
              <li
                key={guardian.id}
                className="flex flex-col gap-2 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="truncate text-sm font-medium">{guardian.invitedEmail}</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">
                      {RELATIONSHIP_LABELS[guardian.relationshipType]}
                    </Badge>
                    <Badge variant={guardian.accessScope === 'FULL_READ' ? 'default' : 'accent'}>
                      {ACCESS_SCOPE_LABELS[guardian.accessScope]}
                    </Badge>
                    <Badge variant={statusVariant(guardian.status)}>
                      {STATUS_LABELS[guardian.status]}
                    </Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    Diundang {new Date(guardian.invitedAt).toLocaleDateString('id-ID')}
                    {guardian.acceptedAt !== null &&
                      ` · Diterima ${new Date(guardian.acceptedAt).toLocaleDateString('id-ID')}`}
                  </span>
                </div>
                {guardian.status === 'ACTIVE' &&
                  (canManage || guardian.guardianUserId === user?.userId) && (
                    <RevokeGuardianButton
                      studentId={studentId}
                      guardianId={guardian.id}
                      invitedEmail={guardian.invitedEmail}
                    />
                  )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <InviteGuardianDialog
        studentId={studentId}
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={setSuccessResult}
      />
      <InvitationSuccessDialog
        result={successResult}
        onClose={() => {
          setSuccessResult(null);
        }}
      />
    </Card>
  );
}
